import { ANALYTICS_OPT_OUT_KEY, allowsWebAnalytics } from "./analytics-policy.ts";

export const ANALYTICS_CONSENT_KEY = "stoptrading.analytics.consent.v1";
export const ANALYTICS_CONSENT_EVENT = "backtest:analytics-consent";
export type AnalyticsConsentChoice = "unknown" | "granted" | "denied";
export type AnalyticsConsentStorage = Pick<Storage, "getItem" | "setItem">;

type ConsentWindow = Window & { __stopTradingAnalyticsConsent?: AnalyticsConsentChoice; doNotTrack?: string };

export function readAnalyticsConsent(storage?: AnalyticsConsentStorage): AnalyticsConsentChoice {
  try {
    const value = storage?.getItem(ANALYTICS_CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : "unknown";
  } catch { return "unknown"; }
}

export function persistAnalyticsConsent(choice: Exclude<AnalyticsConsentChoice, "unknown">, storage?: AnalyticsConsentStorage): boolean {
  try {
    if (!storage) return false;
    storage.setItem(ANALYTICS_CONSENT_KEY, choice);
    return true;
  } catch { return false; }
}

function browserStorage(): AnalyticsConsentStorage | undefined {
  try { return window.localStorage; } catch { return undefined; }
}

export function getAnalyticsConsent(): AnalyticsConsentChoice {
  if (typeof window === "undefined") return "unknown";
  return (window as ConsentWindow).__stopTradingAnalyticsConsent ?? readAnalyticsConsent(browserStorage());
}

export function setAnalyticsConsent(choice: Exclude<AnalyticsConsentChoice, "unknown">): void {
  if (typeof window === "undefined") return;
  (window as ConsentWindow).__stopTradingAnalyticsConsent = choice;
  persistAnalyticsConsent(choice, browserStorage());
  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT));
}

/** Privacy/QA controls take precedence over a saved opt-in. */
export function analyticsContextPermitted(): boolean {
  if (typeof window === "undefined") return false;
  let sessionOptOut = false;
  try {
    if (new URL(window.location.href).searchParams.get("analytics") === "off") {
      window.sessionStorage.setItem(ANALYTICS_OPT_OUT_KEY, "1");
    }
    sessionOptOut = window.sessionStorage.getItem(ANALYTICS_OPT_OUT_KEY) === "1";
  } catch { /* A URL opt-out still applies when storage is unavailable. */ }
  return allowsWebAnalytics({
    href: window.location.href,
    doNotTrack: navigator.doNotTrack || (window as ConsentWindow).doNotTrack,
    globalPrivacyControl: (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl,
    automated: navigator.webdriver,
    development: import.meta.env.DEV,
    disabled: import.meta.env.VITE_ANALYTICS_ENABLED === "false",
    sessionOptOut,
    consentGranted: true,
  });
}

export function measurementAllowed(): boolean {
  return getAnalyticsConsent() === "granted" && analyticsContextPermitted();
}

export function subscribeAnalyticsConsent(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const changedInAnotherTab = (event: StorageEvent) => {
    if (event.key !== ANALYTICS_CONSENT_KEY && event.key !== null) return;
    delete (window as ConsentWindow).__stopTradingAnalyticsConsent;
    listener();
  };
  window.addEventListener(ANALYTICS_CONSENT_EVENT, listener);
  window.addEventListener("storage", changedInAnotherTab);
  return () => {
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, listener);
    window.removeEventListener("storage", changedInAnotherTab);
  };
}
